<!-- @format -->
<script lang="ts">
    import type { StoresKey } from "~/stores";
    import { getStoresContext } from "~/stores";
    import { normalizeURL } from "~/util/store";
    import { parseExceptionalOpeningHoursItem, parseDateISO, formatDate, toRangeOfDaysOfTheWeek } from "~/util/date-n-time"
    
    export let request_store_key: StoresKey;
    export let fragment: Fragment;

    const { T, store, locale, reverse_router, site } = getStoresContext(request_store_key);

    let exceptional_openinghours_formatted:{ date: string, hours: string, fromHour: number, fromMinute: number, open: boolean, reason: string, toHour: number, toMinute: number}[];
    let hours:string[] = [];
    let days:string[] = [];

    let images: string[] = [];
    let imageIndex = 0;
    let displayedImage: string | undefined = '';
    $: isRestaurant = $store?.isRestaurant;
    $: hasLunchMenu = $store?.hasLunchMenu;
    $: homepage = normalizeURL($store?.homepage, "http");
    $: email = normalizeURL($store?.email, "email");
    $: phone = normalizeURL($store?.phone, "tel");
    $: mapHref = $store?.spaceCode? `${$reverse_router.routePurpose("floor map")}?poi=${$store?.spaceCode}` : undefined;
    $: lunchTodayHref = `${$reverse_router.routePurpose("lunch")}?store=${$store?.id}&sort=today`;
    $: lunchWeekHref = `${$reverse_router.routePurpose("lunch")}?store=${$store?.id}&sort=week`;
    $: t = $T;
    $: {
        if($store?.logoURL) {
            images = [$store.logoURL]
        }
        if ($store?.images && $store?.images?.length) { 
            images.push(...$store.images)
        } else {
            displayedImage = $site.logo;
        }
        displayedImage = images && images.length? images[0] : $site.logo;
        if ($store && $locale) {
            if ($store.tenantImages && $store.tenantImages.length >= 1 ) {
                $store.tenantImages.forEach(function (item) {
                    images?.push(item.url)
                });
            }
            $store.unformattedOpeningHours.map((item)=> {
                if ('days' in item) {
                    days.push(toRangeOfDaysOfTheWeek(item.days, $locale));
                    if (item.openingStatus === 'OPEN' && item.dailyOpeningHours && item.dailyOpeningHours.length > 0) {
                        hours.push(`${item.dailyOpeningHours[0].from}-${item.dailyOpeningHours[0].to}`)
                    } else {
                        hours.push(t`stores.closed`)
                    }
                }
            })
            exceptional_openinghours_formatted = ($store.exceptionalOpeningHours || []).slice(0, 30)
            .map((item) => {
                return {
                    ...item,
                    date: formatDate(parseDateISO(item.date), $locale, { day: 'numeric', month: 'numeric' }),
                    hours:  parseExceptionalOpeningHoursItem($locale, item)
                };
            });
        }
    }
    function nextImage() {
        if(images && images.length){
            imageIndex++;
            displayedImage = images[imageIndex];     
        }
    }
    function prevImage() {
        if(images && images.length){
            imageIndex--;
            displayedImage = images[imageIndex];
        }
    }
    function changeImage(index:number){
        imageIndex = index;
        displayedImage = images[imageIndex];
    };

</script>

{#if $store}
    <div class="store-info-container">
        <div class="col-12 col-md-5">
            {#if displayedImage}
                <div class="image-wrapper">
                    <img class="main-image" src={displayedImage} alt=''/>
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                    {#if imageIndex < images.length - 1}
                        <img class="right-arrow-icon" src='/theme/ainoa2024/images/right-arrow-background.png' alt=''  on:click={() => nextImage()}/>    
                    {/if}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                    {#if imageIndex > 0}
                        <img class="left-arrow-icon" src='/theme/ainoa2024/images/left-arrow-background.png' alt='' on:click={() => prevImage()}/>        
                    {/if}
                </div>
            {/if}
            <div class="row images-row d-none d-md-flex d-lg-flex">
                {#if images && images.length}
                    {#each images as image, index}
                        <button class="thumbnail-wrapper" 
                            on:click={() => {
                                changeImage(index);
                            }}>
                            <img src={image} alt='' />
                        </button>
                        
                    {/each}
                {/if}
            </div>
        </div>
        <div class="info-container col-12 col-md-7">
            <h1 class="title">{$store.name}</h1>
            <div class="upper-info">
                <div class="info-row">
                    <div class="title-info">{t`stores.whereAreWe`}</div>
                    <div class="content">{$store?.floor?.description? $store.floor.description : ''} | 
                    {#if mapHref}
                        <a href={mapHref}>{t`stores.seeLocationOnMap`}</a>
                    {/if}
                </div>
                </div>
                <div class="info-row">
                    <div class="title-info">{t`stores.open`}</div>
                    <div class="content">
                        <div class="row">
                            <div class="col-5">
                                {#each days as day}
                                    <p class="text-lowercase text-nowrap"> {day} </p>
                                {/each}
                            </div>
                            <div class="col-7">
                                {#each hours as hour}
                                    <p class="text-lowercase text-nowrap"> {hour} </p>
                                {/each}
                            </div>
                        </div>
                    </div>
                </div>
                {#if exceptional_openinghours_formatted.length > 0}
                    {#each exceptional_openinghours_formatted as item}
                        <div class="info-row">
                            <div class="title-info">{item.reason}  <span>{item.date}</span></div>
                            <div class="content text-lowercase">
                                {@html item.open ? item.hours.replace(/(\d{2})/g, '$1:00') : t`stores.closed`}
                            </div>
                        </div>
                    {/each}
                {/if}
                {#if $store.email || $store.phone || $store.homepage}
                    <div class="info-row">
                        <div class="title-info">{t`stores.contact`}</div>
                        <div class="content d-flex flex-column">
                            <a href={phone.href}>{phone.text}</a>
                            <a href="mailto:{email.href}">{email.text}</a>
                            <a href={homepage.href} target="_blank">{homepage.text}</a>
                        </div>
                    </div>
                {/if}
            </div>
            {#if isRestaurant}
                <div class="lunch-buttons">
                    {#if hasLunchMenu}
                        <a href={lunchTodayHref} class="lunch-button green-button">{t`lunch.today-lunch`}</a>
                        <a href={lunchWeekHref} class="lunch-button green-button">{t`lunch.week-lunch`}</a>
                    {/if}
                {#if $store?.aLaCarteMenu}
                    <a href={$store.aLaCarteMenu} target="_blank" class="lunch-button white-button">
                        {t`lunch.downloadMenu`} (PDF)
                    </a>
                {/if}
                </div>
            {/if}
            <div class="separator"></div>
            <div>{@html $store.description.replace(/(\r\n|\n\r|\r|\n)/g, "<br>")}</div>
        </div>
    </div>
{/if}

<style lang="scss">
    .store-info-container {
        display: flex;
        flex-direction: row;
        margin: 200px 0 50px 0;
        @media screen and (max-width: 767px) {
            flex-direction: column;
        }
        .image-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 380px;
            padding: 1.5rem 1rem;
            background: transparent;
            border: 1px solid var(--color-brand-primary);
            .main-image {
                max-height: 100%;
                max-width: 100%; 
            }
            .right-arrow-icon {
                position: absolute;
                right: -20px;
                width: 70px;
                cursor: pointer;
            }
            .left-arrow-icon {
                position: absolute;
                left: -20px;
                width: 70px;
                cursor: pointer;
            }
        }
        .images-row {
            height: 72px;
            margin: 16px 0 0 0;
            .thumbnail-wrapper {
                background-color:transparent; 
                border: none; 
                cursor: pointer;
                height:100%;
                img {
                    height: 100%;
                    max-width: 150px;
                    margin: 0 10px 0 0;
            }
            }
        }
        .info-container {
            padding: 20px 60px;
            font-size: 16px;
            line-height: 1.5;
            @media screen and (max-width: 767px) {
                padding: 30px 20px;
            }
            a {
                text-decoration: underline;
            }
            .title {
                margin: 0 0 30px 0;
            }
            .upper-info {
                display: flex;
                flex-direction: column;
                gap: 10px;
                .info-row {
                    display: flex;
                    flex-direction: row;
                    @media screen and (max-width: 767px) {
                        flex-direction: column;
                    }
                    .title-info {
                        font-weight: 600;
                        width: 20%;
                        hyphens: auto;
                        @media screen and (max-width: 1330px) {
                            width: 40%;
                        }
                        @media screen and (max-width: 767px) {
                            width: 100%;
                        }
                    }
                    .content {
                        flex: 1;
                        padding: 0 0 0 10px;
                        width: 80%;
                        @media screen and (max-width: 1330px) {
                            width: 60%;
                        }
                        @media screen and (max-width: 767px) {
                            padding: 0;
                            width: 100%;
                        }
                    }
                }
            }
            .lunch-buttons {
                margin: 50px 0;
                .lunch-button {
                    display: inline-block;
                    width: fit-content;
                    border: 1px solid var(--color-brand-primary);
                    padding: 15px 30px;
                    margin: 10px 0 0 10px;
                    text-decoration: none;
                    text-transform: uppercase;
                    cursor: pointer;
                }
                .white-button {
                    background-color: transparent;
                    color: var(--color-brand-primary);
                }
                .green-button {
                    background-color: var(--color-brand-primary);
                    color: var(--color-accent-white);
                }
            }
            .separator {
                border-bottom: 1px solid var(--color-brand-primary);
                margin: 40px 0;
            }
        }
    }
</style>
